// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  updatePassword,
  updateEmail,
  deleteUser,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, deleteDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { saveProfile, loadProfile, markSignupVerified, isSignupVerifiedLocal, isOnboardingDone } from '../services/profileService';
import { savePublicProfile, listIncomingRequests, listSentRequests } from '../services/socialService';
import { sendSecurityNoticeEmail } from '../services/email';
import { requestOtp, verifyOtp, clearOtp } from '../services/otpService';
import { trackSignupCompleted } from '../services/analytics';

// A random token stashed in localStorage so returning sign-ins can be
// recognized as "this browser before." Not real device fingerprinting —
// clearing site data or using a private/incognito window will always look
// like a new device, which is an accepted, honest tradeoff (no backend
// means no server-side device intelligence is possible here).
function getDeviceId() {
  let id = localStorage.getItem('elpis_device_id');
  if (!id) {
    id = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('elpis_device_id', id);
  }
  return id;
}

function reportUrlFor(uid, eventType) {
  return `${window.location.origin}/report-activity?uid=${uid}&event=${eventType}`;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

// Best-effort security log — device/time/IP/approx-location, disclosed in
// Terms section 10 / Privacy Policy. Never throws into the caller; a
// failed log write must never block or error out a real sign-in.
async function logLogin(user, deviceId, isNewDevice) {
  try {
    const token = await user.getIdToken();
    await fetch(`${BACKEND_URL}/api/log-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ deviceId, userAgent: navigator.userAgent, isNewDevice }),
    });
  } catch (err) {
    console.error('logLogin failed (non-blocking):', err.message);
  }
}

// Parked for now: the extra code on an unrecognised device. Only the one-time
// signup code is asked. Flip to true to bring the new-device check back.
const NEW_DEVICE_OTP_ENABLED = false;

const AuthContext = createContext();

const ADMIN_EMAIL = 'atharvajoshi0102@gmail.com';
const TIER_MAP = { [ADMIN_EMAIL]: 'pro' };

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tier, setTier] = useState('free');
  const [loading, setLoading] = useState(true);
  // { purpose: 'signup' | 'new-device' } while an access-blocking OTP is
  // outstanding, else null. Email-change verification is handled inline in
  // Settings instead — it doesn't lock the rest of the app.
  const [otpRequired, setOtpRequired] = useState(null);

  // Last profile we KNOW is real, per uid — a ref (not state) so the
  // onAuthStateChanged closure below always sees the latest value despite
  // its empty dependency array. This is what lets a transient read failure
  // fall back to "keep what we had" instead of "pretend this is a new
  // account" (see loadProfile's fix note and the one below).
  const lastKnownProfileRef = useRef(null);
  // True when the profile read failed and we are showing a stand-in; the
  // stand-in must never be written to Firestore.
  const profileUnreadableRef = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          setCurrentUser(user);
          // Real bug fixed here (2026-09-30): onAuthStateChanged fires
          // more than once per session (token refresh re-fires it, not
          // just real sign-in/out) — and a Firestore read landing in that
          // narrow window before the SDK's refreshed token is attached can
          // transiently permission-deny even for a genuinely signed-in
          // user. loadProfile now throws instead of swallowing that into a
          // fake null, so it's caught here specifically: retry once, and
          // if it still fails, fall back to the last profile we know is
          // real (lastKnownProfileRef) rather than the brand-new-user
          // default. This is what previously reset a fully onboarded
          // returning user back to onboarding step 1 and re-triggered the
          // signup OTP, right after they'd already correctly passed the
          // new-device check with their real, complete profile.
          let existingProfile;
          let readFailed = false;
          try {
            existingProfile = await loadProfile(user.uid);
          } catch (err) {
            console.error('Profile read failed once, retrying:', err);
            try {
              existingProfile = await loadProfile(user.uid);
            } catch (err2) {
              console.error('Profile read failed again — falling back to last known good profile, not a fresh default:', err2);
              existingProfile = lastKnownProfileRef.current || null;
              readFailed = true;
            }
          }
          if (existingProfile) lastKnownProfileRef.current = existingProfile;
          // A failed read is not a new account: never write defaults back over
          // the real document (see the sync effect below).
          profileUnreadableRef.current = readFailed && !existingProfile;
          const profile = existingProfile || {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || 'Student',
            // No username yet — chosen as the first step of onboarding
            // (ProfileSetup.js), reserved for real against the usernames/
            // collection so it's actually unique, not just generated.
            tier: TIER_MAP[user.email] || 'free',
            createdAt: new Date().toISOString(),
            welcomeEmailSent: true,
            profileSetupComplete: false,
            signupVerified: false,
            tutorialSeen: false,
          };
          setUserProfile(profile);
          setTier(TIER_MAP[user.email] || profile.tier || 'free');
          // Real bug fixed here (Aug 19, 2026): this used to gate on
          // `!existingProfile` alone — but the profile doc gets written to
          // Firestore the moment `userProfile` state is set (see the
          // saveProfile effect below), BEFORE OTP is ever verified. So a
          // user who closed the app on the OTP screen and reopened would
          // find `existingProfile` now truthy, fall into the "returning
          // user" branch, and sail straight through — the new-device check
          // there is itself gated on `profileSetupComplete`, which is still
          // false mid-onboarding. `signupVerified` is the real, persisted
          // signal for "did this account ever actually complete the signup
          // OTP" — `profileSetupComplete` is the fallback for accounts that
          // existed before this field did (they clearly got through the old
          // flow already, so they're grandfathered in rather than locked out).
          const signupVerified = (profile.signupVerified ?? profile.profileSetupComplete ?? false)
            || isSignupVerifiedLocal(user.uid)
            || isOnboardingDone(user.uid, profile)
            || readFailed;
          if (signupVerified) markSignupVerified(user.uid);
          if (!signupVerified) {
            // Only track once, on true first creation — not on every retry.
            if (!existingProfile) {
              trackSignupCompleted({ method: user.providerData[0]?.providerId || 'unknown' });
            }
            // One email, not two — the OTP rides along on the welcome
            // send. Blocks the rest of the app until confirmed.
            requestOtp(user.uid, 'signup', user.email, profile, { viaWelcomeEmail: true });
            setOtpRequired({ purpose: 'signup', targetEmail: user.email });
          } else {
            // Returning, verified user — check whether this browser has
            // been seen before for this account. Only meaningful once
            // onboarding is actually done (a brand-new account mid-
            // onboarding hasn't finished the signup OTP yet, so this would
            // double-gate it — already excluded by the branch above).
            const deviceId = getDeviceId();
            const known = profile.knownDeviceIds || [];
            const isNewDevice = NEW_DEVICE_OTP_ENABLED && profile.profileSetupComplete && !known.includes(deviceId);
            if (isNewDevice) {
              requestOtp(user.uid, 'new-device', user.email, profile);
              setOtpRequired({ purpose: 'new-device', targetEmail: user.email, deviceId });
              sendSecurityNoticeEmail(
                profile,
                'A sign-in to your Elpis account was just attempted from a device we haven\'t seen before. If this was you, verify it with the code we sent. If not, use the link below.',
                reportUrlFor(user.uid, 'new-device')
              );
            }
            // Fire-and-forget — never blocks sign-in on the log write or
            // the geolocation lookup. See TermsPage.js section 10 for the
            // disclosure this data collection is covered by.
            logLogin(user, deviceId, isNewDevice);
          }
        } else {
          setCurrentUser(null);
          setUserProfile(null);
          setTier('free');
        }
      } catch (err) {
        // Falls back to guest state rather than leaving `loading` stuck true
        // forever — a hung/failed Firestore read here previously meant
        // setLoading(false) below never ran, which left the app's loading
        // screen unable to ever finish (see App.js Loader for the other
        // half of this fix).
        console.error('Auth state resolution failed, falling back to guest:', err);
        setCurrentUser(null);
        setUserProfile(null);
        setTier('free');
      } finally {
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  // Real bug fixed here (Aug 24 follow-up): this used to saveProfile(the
  // ENTIRE local userProfile object) on every single state change. That's a
  // `setDoc(..., {merge:true})`, and Firestore treats array fields as
  // atomic on merge (no deep-merge) — so if this effect ever fired with a
  // stale local `knownDeviceIds` (e.g. a second onAuthStateChanged/token-
  // refresh re-set userProfile from an in-flight Firestore read that
  // predated the OTP-gate's arrayUnion write), it would silently overwrite
  // the just-verified device back out of Firestore. Next reload: unknown
  // device again, forever, with the OTP loop as the only visible symptom.
  // Fix: never let this generic "keep Firestore in sync with local edits"
  // path touch the handful of fields that firestore.rules deliberately
  // lock to their own narrow, rule-gated write paths (see the profiles
  // update rule) — those fields must only ever change through
  // verifyGateOtp/reactivateAccount/etc, never through a blanket resync.
  const SECURITY_FIELDS = ['knownDeviceIds', 'signupVerified', 'suspended', 'suspendedAt', 'suspendedReason', 'suspendedDetail', 'suspendedBy', 'securityFlagged'];
  useEffect(() => {
    if (currentUser && userProfile && !profileUnreadableRef.current) {
      const syncable = { ...userProfile };
      SECURITY_FIELDS.forEach((f) => delete syncable[f]);
      saveProfile(currentUser.uid, syncable);
      if (userProfile.profileSetupComplete) savePublicProfile(currentUser.uid, userProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return result.user;
  }

  async function loginWithEmail(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  }

  // No custom email template needed — Firebase Auth sends its own reset
  // email and hosts the reset-confirmation page itself, so there's no new
  // backend endpoint or EmailJS template here, unlike the OTP flows above.
  async function resetPassword(email) {
    await sendPasswordResetEmail(auth, email);
  }

  async function signupWithEmail(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    return result.user;
  }

  function getRecaptchaVerifier(containerId) {
    if (!window._lpRecaptchaVerifier) {
      window._lpRecaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible',
      });
    }
    return window._lpRecaptchaVerifier;
  }

  async function sendPhoneCode(phoneNumber, containerId) {
    const verifier = getRecaptchaVerifier(containerId);
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, verifier);
      return confirmationResult;
    } catch (err) {
      // A failed send can leave the invisible reCAPTCHA in a used state —
      // reset it so the next attempt gets a fresh challenge instead of
      // silently failing forever.
      if (window._lpRecaptchaVerifier) {
        window._lpRecaptchaVerifier.clear();
        window._lpRecaptchaVerifier = null;
      }
      throw err;
    }
  }

  async function confirmPhoneCode(confirmationResult, code) {
    const result = await confirmationResult.confirm(code);
    return result.user;
  }

  async function logout() {
    await signOut(auth);
  }

  // Every sensitive account action (password change, email change, suspend,
  // delete) needs a fresh credential first — Firebase rejects those calls
  // with auth/requires-recent-login otherwise. Password accounts reauth with
  // their password; Google-only accounts have no password to check, so they
  // reauth via a fresh Google popup instead.
  function isPasswordAccount() {
    return currentUser?.providerData?.[0]?.providerId === 'password';
  }

  async function reauthenticate(password) {
    if (!currentUser) throw new Error('Not signed in');
    if (isPasswordAccount()) {
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
    } else {
      await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
    }
  }

  async function changePassword(currentPassword, newPassword) {
    await reauthenticate(currentPassword);
    await updatePassword(currentUser, newPassword);
    sendSecurityNoticeEmail(
      userProfile,
      'Your Elpis account password was just changed.',
      reportUrlFor(currentUser.uid, 'password-change')
    );
  }

  // Two-step, using our own OTP rather than Firebase's click-a-link flow —
  // step 1 reauths and sends a 6-digit code to the NEW address; step 2
  // (confirmEmailChange) verifies it and only then actually swaps the
  // email over. Nothing changes until the code is entered.
  async function startEmailChange(currentPassword, newEmail) {
    await reauthenticate(currentPassword);
    await requestOtp(currentUser.uid, 'email-change', newEmail, userProfile);
  }

  async function confirmEmailChange(code, newEmail) {
    const ok = await verifyOtp(currentUser.uid, code);
    if (!ok) throw Object.assign(new Error('Incorrect code'), { code: 'otp/incorrect' });
    await updateEmail(currentUser, newEmail);
    await clearOtp(currentUser.uid);
    sendSecurityNoticeEmail(
      { ...userProfile, email: newEmail },
      `Your Elpis account email was just changed to this address.`,
      reportUrlFor(currentUser.uid, 'email-change')
    );
  }

  async function suspendAccount(currentPassword, reason, detail) {
    await reauthenticate(currentPassword);
    await setUserProfile({
      ...userProfile,
      suspended: true,
      suspendedAt: new Date().toISOString(),
      suspendedReason: reason,
      suspendedDetail: detail || '',
      suspendedBy: 'self',
    });
    await saveProfile(currentUser.uid, {
      suspended: true,
      suspendedAt: new Date().toISOString(),
      suspendedReason: reason,
      suspendedDetail: detail || '',
      suspendedBy: 'self',
    });
    await signOut(auth);
  }

  // Self-service only — an admin-issued suspension (userProfile.suspendedBy
  // === 'admin') must never be liftable by the suspended user themselves,
  // only from the admin console. Guarded here too, not just in the UI that
  // calls this, since this function is the actual point of effect.
  async function reactivateAccount() {
    if (!currentUser) return;
    if (userProfile?.suspendedBy === 'admin') {
      throw new Error('This account was suspended by an admin and can only be reactivated by them.');
    }
    await saveProfile(currentUser.uid, {
      suspended: false,
      suspendedAt: null,
      suspendedReason: null,
      suspendedDetail: null,
      suspendedBy: null,
    });
  }

  async function deleteAccount(currentPassword, reason, detail) {
    if (!currentUser) throw new Error('Not signed in');
    const uid = currentUser.uid;
    await reauthenticate(currentPassword);
    // Sent before the profile doc is gone (need the email address off it)
    // and with no "this wasn't me" report link — unlike password/email
    // changes, there's no live account left afterward to sign back into
    // and act on a report, so that mechanism doesn't apply here. Just a
    // plain confirmation that it happened, matching the exact-phrase
    // confirmation already required to reach this point.
    sendSecurityNoticeEmail(userProfile, 'Your Elpis account has been permanently deleted, as requested. If you didn\'t do this, there\'s unfortunately nothing to recover — accounts are deleted immediately and can\'t be restored.', '');
    // Clean up anything referencing this account elsewhere — a deleted
    // account should leave no "ghost" left behind for other users to see
    // (a pending teammate request from/to someone who no longer exists).
    // Best-effort throughout —
    // one of these failing shouldn't block the actual account deletion.
    const [incoming, sent] = await Promise.all([
      listIncomingRequests(uid).catch(() => []),
      listSentRequests(uid).catch(() => []),
    ]);
    await Promise.all([
      ...incoming.map(r => deleteDoc(doc(db, 'connectionRequests', r.id)).catch(() => {})),
      ...sent.map(r => deleteDoc(doc(db, 'connectionRequests', r.id)).catch(() => {})),
    ]);
    await deleteDoc(doc(db, 'profiles', uid)).catch(() => {});
    await deleteDoc(doc(db, 'publicProfiles', uid)).catch(() => {});
    await deleteUser(currentUser);
  }

  // For the access-blocking gate only (signup / new-device) — email-change
  // verification is handled locally in Settings via otpService directly.
  //
  // Real bug fixed here (Aug 24, 2026): this used to call the shared
  // saveProfile() helper, which SILENTLY SWALLOWS Firestore errors (just
  // console.error, never throws/returns failure). If the knownDeviceIds
  // append ever failed for any reason, the code still optimistically
  // updated local state AND cleared the OTP doc — so the UI looked
  // unblocked for that session, but the write never actually persisted.
  // Next reload, the fresh profile fetch showed the device as still
  // unrecognized, triggering another new-device OTP — forever, with no
  // visible error anywhere. Now uses a direct updateDoc call so a failure
  // actually throws, is caught here, and the OTP gate stays up with a
  // real error instead of silently pretending to succeed.
  async function verifyGateOtp(code) {
    if (!currentUser || !otpRequired) return false;
    const ok = await verifyOtp(currentUser.uid, code);
    if (!ok) return false;
    // Remember on this browser first so a failed profile write can never
    // bring the signup code back on the next load.
    if (otpRequired.purpose === 'signup') markSignupVerified(currentUser.uid);
    try {
      if (otpRequired.purpose === 'new-device' && otpRequired.deviceId) {
        // Order matters: the profiles/{uid} update rule only allows growing
        // knownDeviceIds while otps/{uid}.verified == true, so this append
        // must happen BEFORE clearing the OTP doc — clearing first would
        // make the security rule reject the very write it's meant to allow.
        await updateDoc(doc(db, 'profiles', currentUser.uid), { knownDeviceIds: arrayUnion(otpRequired.deviceId) });
        setUserProfile({ ...userProfile, knownDeviceIds: [...(userProfile?.knownDeviceIds || []), otpRequired.deviceId] });
      }
      if (otpRequired.purpose === 'signup') {
        // Same ordering requirement as knownDeviceIds above — the rule only
        // allows signupVerified to flip true while otps/{uid}.verified == true.
        await updateDoc(doc(db, 'profiles', currentUser.uid), { signupVerified: true });
        setUserProfile({ ...userProfile, signupVerified: true });
      }
    } catch (err) {
      console.error('OTP gate: failed to persist verification', err);
      if (otpRequired.purpose !== 'signup') return false;
    }
    // Closes the window back down — otherwise `verified: true` would sit
    // on the otps doc indefinitely and leave knownDeviceIds permanently
    // writable by anything with a valid session. Only reached once the
    // profile write above genuinely succeeded.
    await clearOtp(currentUser.uid);
    setOtpRequired(null);
    return true;
  }

  async function resendGateOtp() {
    if (!currentUser || !otpRequired) return;
    await requestOtp(currentUser.uid, otpRequired.purpose, otpRequired.targetEmail, userProfile, { viaWelcomeEmail: otpRequired.purpose === 'signup' });
  }

  function setUserProfileWrapper(updates) {
    setUserProfile({
      ...userProfile,
      ...updates,
    });
  }

  return (
    <AuthContext.Provider value={{
      currentUser,
      userProfile,
      setUserProfile: setUserProfileWrapper,
      tier,
      loading,
      loginWithGoogle,
      loginWithEmail,
      resetPassword,
      signupWithEmail,
      sendPhoneCode,
      confirmPhoneCode,
      logout,
      isPasswordAccount,
      reauthenticate,
      changePassword,
      startEmailChange,
      confirmEmailChange,
      suspendAccount,
      reactivateAccount,
      deleteAccount,
      otpRequired,
      verifyGateOtp,
      resendGateOtp,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
